import React, { useEffect, useState } from 'react';
import { Bell, Loader2, Send, History } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { adminSendNotification, ManualSendPayload, fetchBroadcasts, BroadcastLog } from '../../features/notifications/api/notifications';
import { NOTIFICATIONS } from '../../constants/testIds';

const inputClass = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';
const ROLES = ['member', 'vip', 'mentor', 'sales'];

const targetLabel = (b: BroadcastLog) => b.target_type === 'all' ? 'Semua Member' : b.target_type === 'role' ? `Role: ${b.role}` : 'User tertentu';

export default function AdminNotificationsPage() {
  const [targetType, setTargetType] = useState<'all' | 'role' | 'user'>('all');
  const [role, setRole] = useState('member');
  const [userId, setUserId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [history, setHistory] = useState<BroadcastLog[]>([]);

  const loadHistory = () => { fetchBroadcasts().then(setHistory).catch(() => {}); };
  useEffect(() => { loadHistory(); }, []);

  const send = async () => {
    setSending(true);
    try {
      const payload: ManualSendPayload = { target_type: targetType, title, message, send_email: sendEmail };
      if (targetType === 'role') payload.role = role;
      if (targetType === 'user') payload.user_id = userId.trim();
      const res: any = await adminSendNotification(payload);
      toast.success(`Terkirim ke ${res.sent} pengguna${res.email_sent ? ` · ${res.email_sent} email` : ''}`);
      setTitle(''); setMessage(''); loadHistory();
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSending(false); }
  };

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Kirim Notifikasi">
      <div className="max-w-xl" data-testid={NOTIFICATIONS.adminPage}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><Bell className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
          <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Broadcast</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Kirim Notifikasi</h2></div>
        </div>

        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-4">
          <div>
            <label className={labelClass}>Target</label>
            <select data-testid={NOTIFICATIONS.target} className={inputClass} value={targetType} onChange={(e) => setTargetType(e.target.value as any)}>
              <option value="all">Semua Member</option>
              <option value="role">Berdasarkan Role</option>
              <option value="user">User Tertentu (ID)</option>
            </select>
          </div>
          {targetType === 'role' && (
            <div>
              <label className={labelClass}>Role</label>
              <select data-testid={NOTIFICATIONS.role} className={inputClass} value={role} onChange={(e) => setRole(e.target.value)}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          {targetType === 'user' && (
            <div>
              <label className={labelClass}>User ID</label>
              <input data-testid={NOTIFICATIONS.user} className={inputClass} value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="MongoDB ObjectId user" />
            </div>
          )}
          <div>
            <label className={labelClass}>Judul</label>
            <input data-testid={NOTIFICATIONS.title} className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Judul notifikasi" />
          </div>
          <div>
            <label className={labelClass}>Pesan</label>
            <textarea data-testid={NOTIFICATIONS.message} className={inputClass} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Isi pesan" />
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer" data-testid="broadcast-send-email">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-cyan-400 w-4 h-4" />
            Kirim juga ke email member (hanya yang sudah verifikasi & belum unsubscribe)
          </label>
          <button data-testid={NOTIFICATIONS.sendBtn} onClick={send} disabled={sending || !title || (targetType === 'user' && !userId.trim())} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Send className="w-4 h-4" strokeWidth={2} />} Kirim
          </button>
        </div>

        <div className="mt-8" data-testid="broadcast-history">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
            <h3 className="text-sm uppercase tracking-wider text-white/70">Riwayat Broadcast ({history.length})</h3>
          </div>
          {history.length === 0 ? (
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 text-center text-white/50 text-sm">Belum ada broadcast terkirim.</div>
          ) : (
            <div className="space-y-2">
              {history.map((b) => (
                <div key={b.id} data-testid="broadcast-row" className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-white font-medium">{b.title}</p>
                      {b.message && <p className="text-sm text-white/50 mt-0.5 whitespace-pre-line">{b.message}</p>}
                    </div>
                    <span className="text-xs text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 px-2 py-0.5 rounded-full shrink-0">{b.sent_count} terkirim</span>
                  </div>
                  <p className="text-xs text-white/40 mt-2">{targetLabel(b)} · {new Date(b.created_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' })} WIB</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
